import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@chakra-ui/react';

export default function ImageZoom({
  src,
  alt = 'Zoomable image',
  width,
  height,
  borderRadius = '10px',
  zoomScale = 2,
  lensColor = 'rgba(0, 0, 0, 0.3)',
  lensSize = 150,
  zoomPosition = 'auto', // 'auto', 'right', 'overlay'
}) {
  const [isZooming, setIsZooming] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [backgroundPosition, setBackgroundPosition] = useState({ x: 0, y: 0 });
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [shouldShowRight, setShouldShowRight] = useState(true);

  const containerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const img = imageRef.current;
    if (!img) {
      return;
    }

    const measure = () => {
      const { width: imgWidth, height: imgHeight } = img.getBoundingClientRect();
      setImageDimensions({ width: imgWidth, height: imgHeight });
    };

    // Measure after image loads to get accurate dimensions
    if (img.complete) {
      measure();
    } else {
      img.addEventListener('load', measure);
      return () => img.removeEventListener('load', measure);
    }
  }, [src, width, height]);

  useEffect(() => {
    // Check if there's enough space to show zoom on the right
    if (containerRef.current && zoomPosition === 'auto') {
      const containerRect = containerRef.current.getBoundingClientRect();
      const spaceOnRight = window.innerWidth - containerRect.right;
      const requiredSpace = containerRect.width + 20;
      setShouldShowRight(spaceOnRight >= requiredSpace);
    } else if (zoomPosition === 'right') {
      setShouldShowRight(true);
    } else if (zoomPosition === 'overlay') {
      setShouldShowRight(false);
    }
  }, [zoomPosition, imageDimensions]);

  const handleMouseMove = (e) => {
    if (!containerRef.current || !imageRef.current) {
      return;
    }

    const imageRect = imageRef.current.getBoundingClientRect();

    // Calculate mouse position relative to image
    const x = e.clientX - imageRect.left;
    const y = e.clientY - imageRect.top;

    // Ensure mouse is within image bounds
    if (x < 0 || x > imageRect.width || y < 0 || y > imageRect.height) {
      return;
    }

    // Calculate lens position (centered on cursor)
    const lensX = Math.max(0, Math.min(x - lensSize / 2, imageRect.width - lensSize));
    const lensY = Math.max(0, Math.min(y - lensSize / 2, imageRect.height - lensSize));

    // Calculate background position for zoom (as percentage)
    const bgX = (lensX / (imageRect.width - lensSize)) * 100;
    const bgY = (lensY / (imageRect.height - lensSize)) * 100;

    setLensPosition({ x: lensX, y: lensY });
    setBackgroundPosition({ x: bgX || 0, y: bgY || 0 });
  };

  const handleMouseEnter = () => {
    setIsZooming(true);
  };

  const handleMouseLeave = () => {
    setIsZooming(false);
  };

  return (
    <Box position="relative" display="inline-block" width={width} height={height}>
      <Box
        ref={containerRef}
        position="relative"
        width="100%"
        height="100%"
        overflow="hidden"
        borderRadius={borderRadius}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        cursor="crosshair"
      >
        <Box
          as="img"
          ref={imageRef}
          src={src}
          alt={alt}
          width="100%"
          height="100%"
          objectFit="contain"
          userSelect="none"
          pointerEvents="none"
        />

        {isZooming && (
          <Box
            position="absolute"
            top={`${lensPosition.y}px`}
            left={`${lensPosition.x}px`}
            width={`${lensSize}px`}
            height={`${lensSize}px`}
            backgroundColor={lensColor}
            border="1px solid white"
            borderRadius="50%"
            pointerEvents="none"
            zIndex={1}
            boxShadow="0 0 10px rgba(0, 0, 0, 0.3)"
          />
        )}
      </Box>

      {isZooming && (
        <Box
          position="absolute"
          top={shouldShowRight ? '0' : '50%'}
          right={shouldShowRight ? '-104%' : 'auto'}
          left={shouldShowRight ? 'auto' : '50%'}
          transform={shouldShowRight ? 'none' : 'translate(-50%, -50%) scale(0.95)'}
          width={shouldShowRight ? '100%' : '90%'}
          height={shouldShowRight ? '100%' : '90%'}
          overflow="hidden"
          borderRadius="6px"
          border="1px solid"
          borderColor="white"
          backgroundColor="white"
          boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
          zIndex={10}
          style={{
            backgroundImage: `url(${src})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: `${zoomScale * 100}%`,
            backgroundPosition: `${backgroundPosition.x}% ${backgroundPosition.y}%`,
          }}
        />
      )}
    </Box>
  );
}
